import { useState, useEffect } from 'react';
import { apiFetch } from '../../../auth/apiFetch';

const statusUrl = process.env.REACT_APP_PIPELINE_STATUS_URL
  || process.env.REACT_APP_API_URL
  || window.location.origin;

export function usePipelineStatus() {
  const [prsets, setPrsets] = useState([]);
  const [presetsLoading, setPresetsLoading] = useState(true);
  const [presetsError, setPresetsError] = useState(null);

  useEffect(() => {
    apiFetch(`${statusUrl}/api/pipeline-status/prsets`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(setPrsets)
      .catch((e) => setPresetsError(e.message))
      .finally(() => setPresetsLoading(false));
  }, []);

  const [products, setProducts] = useState(null);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState(null);

  // Reprocessing after a rerun happens asynchronously (S3 event -> Lambda),
  // typically a few minutes — there's no live progress signal to poll, so
  // this only tracks "a rerun was just triggered" for a brief confirmation,
  // not a fake "processing" state we can't actually resolve.
  const [triggeredEans, setTriggeredEans] = useState(new Set());
  const [rerunError, setRerunError] = useState(null);

  const [zipLoading, setZipLoading] = useState(false);
  const [zipError, setZipError] = useState(null);

  const [recheckError, setRecheckError] = useState(null);

  const [downloadingEans, setDownloadingEans] = useState(new Set());
  const [downloadError, setDownloadError] = useState(null);

  // Per-prset counts of items reprocessed-but-not-yet-downloaded, for the
  // dropdown badge — fetched once on mount, and refreshed after a download
  // actually clears some of them (see downloadZip below).
  const [needsPickupCounts, setNeedsPickupCounts] = useState({});
  const [needsPickupCountsError, setNeedsPickupCountsError] = useState(null);

  const loadNeedsPickupCounts = async () => {
    setNeedsPickupCountsError(null);
    try {
      const r = await apiFetch(`${statusUrl}/api/pipeline-status/needs-pickup-counts`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setNeedsPickupCounts(await r.json());
    } catch (e) {
      setNeedsPickupCountsError(e.message);
    }
  };

  useEffect(() => {
    loadNeedsPickupCounts();
  }, []);

  const loadProducts = async (prset) => {
    setProductsLoading(true);
    setProductsError(null);
    setProducts(null);
    setTriggeredEans(new Set());
    try {
      const r = await apiFetch(`${statusUrl}/api/pipeline-status/prset/${encodeURIComponent(prset)}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setProducts(await r.json());
    } catch (e) {
      setProductsError(e.message);
    } finally {
      setProductsLoading(false);
    }
  };

  // Backs the "Run Pipeline" button - asks pipeline-status-service to
  // immediately recheck every already-tracked product in this prset for
  // drift (metadata/cover/source PDF changed) and retrigger anything stale,
  // instead of waiting for the 30-min scheduled sweep. Best-effort: if the
  // recheck request itself fails, still refresh the table with loadProducts
  // so a transient recheck failure doesn't also hide the current state.
  const runPipeline = async (prset) => {
    setRecheckError(null);
    setProductsLoading(true);
    try {
      const r = await apiFetch(`${statusUrl}/api/pipeline-status/prset/${encodeURIComponent(prset)}/recheck`, {
        method: 'POST',
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
    } catch (e) {
      setRecheckError(e.message);
    }
    await loadProducts(prset);
  };

  const rerun = async (ean) => {
    setRerunError(null);
    try {
      const r = await apiFetch(`${statusUrl}/api/pipeline-status/${encodeURIComponent(ean)}/rerun`, {
        method: 'POST',
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setTriggeredEans((prev) => new Set(prev).add(ean));
    } catch (e) {
      setRerunError(e.message);
    }
  };

  const downloadZip = async (eans, prset, filename = prset) => {
    setZipLoading(true);
    setZipError(null);
    try {
      const r = await apiFetch(`${statusUrl}/api/pipeline-status/zip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(eans),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filename}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      // The backend clears needsPickup for every row actually included in the
      // zip — refresh both the row list and the dropdown counts so the
      // "Reprocessed" chip/badge disappear immediately, no manual reload.
      loadNeedsPickupCounts();
      loadProducts(prset);
      return true;
    } catch (e) {
      setZipError(e.message);
      return false;
    } finally {
      setZipLoading(false);
    }
  };

  const downloadOne = async (ean, filename) => {
    setDownloadingEans((prev) => new Set(prev).add(ean));
    setDownloadError(null);
    try {
      const r = await apiFetch(`${statusUrl}/api/pipeline-status/${encodeURIComponent(ean)}/download`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      // Same as downloadZip - the backend clears needsPickup for this ean,
      // so refresh both to drop the "Reprocessed" chip/badge immediately.
      loadNeedsPickupCounts();
      if (products) setProducts((prev) => prev.map((p) => (p.ean === ean ? { ...p, needsPickup: false } : p)));
    } catch (e) {
      setDownloadError(e.message);
    } finally {
      setDownloadingEans((prev) => {
        const next = new Set(prev);
        next.delete(ean);
        return next;
      });
    }
  };

  return {
    prsets, presetsLoading, presetsError,
    products, productsLoading, productsError, loadProducts,
    recheckError, runPipeline,
    triggeredEans, rerunError, rerun,
    zipLoading, zipError, downloadZip,
    downloadingEans, downloadError, downloadOne,
    needsPickupCounts, needsPickupCountsError,
  };
}
